import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Eye, Ban, CheckCircle, Phone, MapPin } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { ROUTES, buildRoute } from '@/constants/routes';
import { useCustomers, useBlockCustomer, useCustomerStates } from '../hooks/useCustomers';
import { useFilters } from '@/hooks/useFilters';
import { usePagination } from '@/hooks/usePagination';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable } from '@/components/common/DataTable';
import { FilterBar } from '@/components/common/FilterBar';
import { BlockUserDialog } from '@/components/common/ConfirmDialog';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dropdown, DropdownItem, DropdownSeparator } from '@/components/ui/dropdown';
import { TablePagination } from '@/components/ui/pagination';
import type { Customer } from '@/types';

const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'blocked', label: 'Blocked' },
];

const activityOptions = [
  { value: 'all', label: 'All Activity' },
  { value: 'booked', label: 'Has Booked' },
  { value: 'never', label: 'Never Booked' },
];

export function CustomersListPage() {
  const navigate = useNavigate();
  const { page, limit, setPage, setLimit } = usePagination();
  const { filters, setFilter, resetFilters } = useFilters({
    defaultValues: {
      search: '',
      status: 'all',
      state: 'all',
      activity: 'all',
    },
  });

  const { data: states } = useCustomerStates();
  const stateOptions = [
    { value: 'all', label: 'All States' },
    ...(states || []).map((s) => ({ value: s.state, label: `${s.state} (${s.count})` })),
  ];

  const [blockDialog, setBlockDialog] = useState<{
    isOpen: boolean;
    customer: Customer | null;
  }>({ isOpen: false, customer: null });

  const { data, isLoading, refetch } = useCustomers({
    search: filters.search,
    status: filters.status as 'all' | 'active' | 'blocked' | undefined,
    state: filters.state,
    activity: filters.activity as 'all' | 'booked' | 'never' | undefined,
    page,
    limit,
  });

  const { mutate: blockCustomer, isPending: isBlocking } = useBlockCustomer();

  const handleBlock = () => {
    if (blockDialog.customer) {
      blockCustomer(
        {
          id: blockDialog.customer.id,
          block: !blockDialog.customer.isBlocked,
        },
        {
          onSuccess: () => {
            setBlockDialog({ isOpen: false, customer: null });
          },
        }
      );
    }
  };

  const columns: ColumnDef<Customer>[] = [
    {
      accessorKey: 'name',
      header: 'Customer',
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <Avatar src={row.original.avatar} alt={row.original.name} size="sm" />
          <div>
            <p className="font-medium">{row.original.name}</p>
            <p className="text-sm text-muted-foreground">{row.original.email}</p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-muted-foreground" />
          {row.original.phone}
        </div>
      ),
    },
    {
      accessorKey: 'state',
      header: 'State',
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 text-sm">
          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
          {row.original.state || <span className="text-muted-foreground">Unknown</span>}
        </div>
      ),
    },
    {
      accessorKey: 'totalBookings',
      header: 'Bookings',
      cell: ({ row }) => row.original.totalBookings,
    },
    {
      accessorKey: 'totalSpent',
      header: 'Total Spent',
      cell: ({ row }) => formatCurrency(row.original.totalSpent),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {row.original.isBlocked ? (
            <Badge variant="destructive">Blocked</Badge>
          ) : row.original.isVerified ? (
            <Badge variant="success">Verified</Badge>
          ) : (
            <Badge variant="muted">Unverified</Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Joined',
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Dropdown
          trigger={
            <Button variant="ghost" size="icon-sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          }
        >
          <DropdownItem
            icon={<Eye className="h-4 w-4" />}
            onClick={() =>
              navigate(buildRoute(ROUTES.CUSTOMER_DETAIL, { id: row.original.id }))
            }
          >
            View Details
          </DropdownItem>
          <DropdownSeparator />
          <DropdownItem
            icon={
              row.original.isBlocked ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <Ban className="h-4 w-4" />
              )
            }
            destructive={!row.original.isBlocked}
            onClick={() =>
              setBlockDialog({ isOpen: true, customer: row.original })
            }
          >
            {row.original.isBlocked ? 'Unblock' : 'Block'}
          </DropdownItem>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customers"
        description="Manage customer accounts and view their activity"
      />

      <FilterBar
        filters={[
          {
            type: 'search',
            key: 'search',
            placeholder: 'Search by name, email, or phone...',
            className: 'w-80',
          },
          {
            type: 'select',
            key: 'status',
            placeholder: 'Status',
            options: statusOptions,
          },
          {
            type: 'select',
            key: 'state',
            placeholder: 'State',
            options: stateOptions,
          },
          {
            type: 'select',
            key: 'activity',
            placeholder: 'Activity',
            options: activityOptions,
          },
        ]}
        values={filters}
        onChange={(key, value) => setFilter(key as keyof typeof filters, value as string)}
        onReset={resetFilters}
        onRefresh={() => refetch()}
      />

      <DataTable
        columns={columns}
        data={data?.data || []}
        loading={isLoading}
        showPagination={false}
        showSearch={false}
        onRowClick={(row) =>
          navigate(buildRoute(ROUTES.CUSTOMER_DETAIL, { id: row.id }))
        }
      />

      {data && (
        <TablePagination
          currentPage={data.pagination.page}
          totalPages={data.pagination.totalPages}
          totalItems={data.pagination.total}
          pageSize={data.pagination.limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
        />
      )}

      <BlockUserDialog
        isOpen={blockDialog.isOpen}
        onClose={() => setBlockDialog({ isOpen: false, customer: null })}
        onConfirm={handleBlock}
        userName={blockDialog.customer?.name || ''}
        isBlocked={blockDialog.customer?.isBlocked || false}
        isLoading={isBlocking}
      />
    </div>
  );
}
